import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Loads several admin resources at once: loaders is { key: () => Promise }. Keys listed in `optional`
 * resolve to null on failure (for example `config/users` for a role without ViewUsers) instead of failing the page.
 */
export function useAdminData(loaders, { optional = [] } = {}) {
  const loadersRef = useRef(loaders);
  loadersRef.current = loaders;
  const optionalRef = useRef(optional);
  optionalRef.current = optional;
  const [state, setState] = useState({ data: null, loading: true, error: null });

  const load = useCallback(async ({ quiet = false } = {}) => {
    if (!quiet) setState((s) => ({ ...s, loading: true, error: null }));
    const entries = Object.entries(loadersRef.current);
    try {
      const values = await Promise.all(
        entries.map(([key, loader]) =>
          loader().catch((err) => {
            if (optionalRef.current.includes(key)) return null;
            throw err;
          })
        )
      );
      const data = Object.fromEntries(entries.map(([key], i) => [key, values[i]]));
      setState({ data, loading: false, error: null });
      return data;
    } catch (error) {
      setState((s) => ({ data: quiet ? s.data : null, loading: false, error }));
      return null;
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const reload = useCallback(() => load({ quiet: true }), [load]);

  return { ...state, retry: load, reload };
}
